/**
 * WEB-2054 (починка ревизии, Р1 и Р4) — виды блоков, которые едут В ЯДРЕ,
 * хотя рисует их не сам `PublicBlockRenderer.tsx`.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ВЕТКИ В ЯДРЕ. Разрез рендерера (коммит 90750a178)
 * увёз в ленивый кусок `extended` две группы видов, которым там не место:
 *
 *  1. ДЕНЬГИ. «Выбор тарифа» (`plan_selector`) кладёт умолчание тарифа в общие
 *     значения формы только на СВОЁМ монтировании, а первая ступень лестницы
 *     цены (`paywall-runtime/resolvePriceId.ts`) читает именно его. Кнопка
 *     оплаты, форма Stripe и кошельки остались в ядре и рисуются сразу —
 *     значит, пока кусок едет, покупатель может нажать «Купить» и уйти в
 *     чекаут с ценой КНОПКИ вместо умолчального тарифа. Вместе с селектором
 *     сюда переехали `payment_terms` и `price_breakdown_text`: они обещают
 *     покупателю сумму той же лестницей, и приезжать позже кнопки они тоже не
 *     имеют права.
 *  2. ЧАСТЫЕ ВИДЫ. `hero`, `bullet_list`, `icon_with_text`, `card`,
 *     `columns_2`, `testimonials` есть почти в каждой воронке — для них
 *     «отложенный» вес не исчезал, а переезжал во ВТОРОЙ запрос на том же
 *     критическом пути.
 *
 * Держать их ветками прямо в `PublicBlockRenderer.tsx` значило бы перенести
 * туда и весь их обиход (карточки тарифов, склейку сумм, подстановку периода)
 * — файл ядра вырос бы вдвое. Поэтому они живут здесь, а ядро тянет этот файл
 * СТАТИЧЕСКИ: в сборке он часть первого экрана, ленивого пути у него нет.
 *
 * Границы те же, что у кусков: вход — общий `PublicExtendedBlockProps`, выход
 * — общая обёртка блока `withExtendedBlockShell`. Совпадение списка видов с
 * ветками этого файла держит двусторонний сторож `publicBlockChunkMap.web2054`.
 */
import React, { useEffect, useState } from 'react';
import { buttonSurfaceStyle } from './blockBackgroundStyle';
import type { PlanSelectorPlan } from '@/store/editorSlice';
import {
  planSelectorDefaultIndex,
  resolvePlanSelectorPlans,
} from '@/player/planSelectorPlans';
import { getStylePackById } from '@/themes/stylePacks';
import type { StylePack } from '@/themes/stylePack.types';
import { interpolate } from '@/player/interpolate';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from './RichTextContent';
import { asRichField, hasRichText } from '@/types/rich-text';
import { useFormValues } from './FormValuesContext';
import { useImageSpaceReserve } from './imageSpaceReserve';
import {
  blockTextRoles,
  isRoleStyled,
  scaledTextRoleStyle,
  type TextRoleStyle,
} from './semanticTextRoles';
import {
  type PublicStripePrice,
  formatStripePrice,
  formatPriceAmount,
  findStripePrice,
} from '@/utils/formatStripePrice';
import {
  derivePriceDisplay,
  isUsableAmount,
  perDayMinorUnits,
  pricePeriodWord,
  substitutePeriodPlaceholder,
} from '@/utils/derivePriceDisplay';
import { primaryButtonStyle } from '@/themes';
import { resolvePaymentTermsPriceId } from '@/components/paywall-runtime/resolvePriceId';
import { getIconEmoji } from './iconEmojiMap';
import { useEditorPreview } from './EditorPreviewContext';
import { resolveBlockBorder } from './blockBorder';
import { TestimonialCard } from './TestimonialCard';
import { resolveTestimonialLook } from './testimonialLook';
import {
  CardImageBackdrop,
  cardBackdropShellStyle,
  isCardImageBackdrop,
} from './cardImageBackdrop';
import { resolveContentTextColor } from './blockInnerSurface';
import { resolveOptionAccent } from './optionAccentColor';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolvePaywallText } from '@/utils/portalLegacyDefaults';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказка АВТОРУ — из маленького словаря подсказок; полный словарь
// конструктора этому файлу нельзя (его рисует публичная страница покупателя).
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { aiFields } from './aiShapedFields';
import type { PublicExtendedBlockProps } from '@/player/publicExtendedBlockProps';
import { withExtendedBlockShell } from './extendedBlockShell';

/**
 * WEB-936 п.1: денежные плейсхолдеры юридического текста. Если хоть один из
 * них есть в шаблоне, а цены нет — фразу нельзя показывать: получится
 * «После -дневного пробного периода… по цене .» (дыры вместо срока и суммы).
 */
const PAYMENT_TERMS_MONEY_PLACEHOLDER =
  /\{\{\s*(price|renewal_price|trial_days)\s*\}\}/;

/**
 * Interpolates {{price}} / {{trial_days}} / {{renewal_price}} placeholders
 * using a resolved Stripe price.
 *
 * Возвращает `null`, когда подставлять нечего: шаблон обещает цену/срок, а
 * цена не резолвится. Вызывающий обязан скрыть блок — юридический текст с
 * дырами недопустим (ревью Apple).
 */
function interpolatePaymentTerms(
  template: string,
  price: PublicStripePrice | undefined,
): string | null {
  if (!price)
    return PAYMENT_TERMS_MONEY_PLACEHOLDER.test(template) ? null : template;
  const formatted = formatStripePrice(price);
  const interpolated = template
    .replace(/\{\{\s*price\s*\}\}/g, formatted)
    .replace(/\{\{\s*renewal_price\s*\}\}/g, formatted)
    .replace(
      // WEB-808: без триала НЕ печатаем литеральный «0» («0 дней триал» врало
      // покупателю) — подставляем пустую строку, текст шаблона свободный.
      /\{\{\s*trial_days\s*\}\}/g,
      price.trialPeriodDays && price.trialPeriodDays > 0
        ? String(price.trialPeriodDays)
        : '',
    );

  // WEB-832 п.2: пустая подстановка оставляла дыру в предложении — двойной
  // пробел и пробел перед знаком препинания («Триал  дней ,»). Схлопываем:
  // шаблон пишет человек, и заставлять его думать о пробелах вокруг
  // плейсхолдера неправильно.
  return interpolated
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();
}

const PLAN_CARD_BORDER = 'var(--color-border-primary, #d9d9d9)';
const PLAN_HIGHLIGHT_BORDER = 'var(--color-primary, #4094F7)';

/**
 * WEB-925 атом 3: акцент выделения тарифа. Разметка читает его через кастомное
 * свойство контейнера — так значение резолвится в одном месте, а карточки и
 * чип остаются «как раньше», если пака нет (фолбэк = сегодняшний бренд).
 */
const PLAN_ACCENT = 'var(--plan-accent, var(--color-primary, #4094F7))';

type PlanHighlight = StylePack['paywall']['planHighlight'];

/** Высота шапки-крышки (BetterMe): карточка резервирует ровно столько сверху. */
const PLAN_CAP_HEIGHT = 22;

/**
 * WEB-925: премиальный акцент для ЗАЛИВОК выделения (чип, крышка, рамка).
 * `accents.premium: 'none'` резолвится в currentColor — для заливки чипа это
 * его собственный белый текст (белое на белом), поэтому «акцента нет» здесь
 * означает сегодняшний бренд, а не currentColor. Для ТЕКСТА/рамок вне тарифов
 * currentColor корректен и используется как есть.
 */
function planAccentValue(stylePackId?: string): string {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  if (!pack || pack.accents.premium === 'none') return PLAN_HIGHLIGHT_BORDER;
  return `var(--accent-premium, ${PLAN_HIGHLIGHT_BORDER})`;
}

/**
 * WEB-1016: цвет подписи НА этой заливке — парная переменная той же роли.
 * Ветка «премиума нет» возвращает заливку к бренду, значит и текст обязан
 * вернуться к брендовому `--color-on-primary`, иначе подпись считалась бы
 * под чужую заливку.
 */
function planAccentOnValue(stylePackId?: string): string {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  if (!pack || pack.accents.premium === 'none')
    return 'var(--color-on-primary, #fff)';
  return 'var(--accent-premium-on, var(--color-on-primary, #fff))';
}

/**
 * WEB-925: три замеренных способа выделения (SYNTHESIS.md §6 п.4).
 * `chip-on-border` — сегодняшний вид (12min), меняться не должен.
 */
function planBadgeStyle(highlight: PlanHighlight): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    fontSize: 11,
    fontWeight: 700,
    // WEB-1016: подпись читается на СВОЕЙ заливке. `--color-on-primary`
    // посчитан для брендовой кнопки, а чип залит премиальным акцентом — на
    // паках, где это разные источники палитры, подпись пропадала.
    color: 'var(--plan-accent-on, var(--color-on-primary, #fff))',
    background: PLAN_ACCENT,
  };
  if (highlight === 'cap-header') {
    // Крышка НАД карточкой (BetterMe): накрывает её вместе с 2px-рамкой,
    // низ квадратный — она смыкается с карточкой.
    return {
      ...base,
      top: -PLAN_CAP_HEIGHT,
      left: -2,
      right: -2,
      height: PLAN_CAP_HEIGHT,
      lineHeight: `${PLAN_CAP_HEIGHT}px`,
      textAlign: 'center',
      borderRadius:
        'var(--border-radius-lg, 12px) var(--border-radius-lg, 12px) 0 0',
    };
  }
  if (highlight === 'chip-on-seam') {
    // Чип на СТЫКЕ карточек (Astroline): сидит по центру верхней грани,
    // половиной высоты заезжая на соседа сверху.
    return {
      ...base,
      top: -11,
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '2px 10px',
      // Пилюля через токен темы: числовой радиус в этом файле запрещён
      // гвардом (радиусы блоков обязаны идти из токенов, не из хардкода).
      borderRadius: 'var(--border-radius-button, 999px)',
      whiteSpace: 'nowrap',
    };
  }
  return {
    ...base,
    top: -10,
    left: 12,
    padding: '2px 10px',
    borderRadius: 'var(--border-radius-md, 10px)',
  };
}

/**
 * WEB-847: минимальная ширина карточки тарифа и зазор сетки.
 *
 * Было `flex: 1 1 calc(50% - 6px)` (WEB-621) — РОВНО две карточки в ряд на
 * любой ширине. Это не адаптивность, а константа: на узком превью карточки
 * жались, а на реальном телефоне раскладка была другой (находка Алекса B2).
 *
 * Стало: `repeat(auto-fit, minmax(150px, 1fr))` — колонки следуют из ширины
 * контейнера. Порог 150px подобран по живым ширинам области контента:
 *   узкий телефон 320 → 268px → 1 колонка (столбик)
 *   обычный      390 → 338px → 2 колонки (как на устройстве Алекса)
 * Запас до перелома (312px) с обеих сторон — десятки пикселей, поэтому
 * небольшая разница в отступах превью и паблика раскладку не переворачивает.
 */
export const PLAN_CARD_MIN_WIDTH = 150;
export const PLAN_GRID_GAP = 12;

/**
 * Сколько карточек встанет в ряд при данной ширине контейнера — формула
 * CSS-сетки auto-fit/minmax, выраженная в коде, чтобы порог можно было
 * проверить тестом, а не «на глаз» в браузере.
 */
export function planCardsPerRow(containerWidth: number): number {
  return Math.max(
    1,
    Math.floor(
      (containerWidth + PLAN_GRID_GAP) / (PLAN_CARD_MIN_WIDTH + PLAN_GRID_GAP),
    ),
  );
}

interface PlanSelectorBlockProps {
  /** WEB-1428: язык воронки для словарного фолбэка цены. */
  locale?: string | null;
  /**
   * WEB-2973: ответы покупателя — для подстановки в тексты автора (бейдж,
   * подпись «нет цены»); без него покупатель видел сырое `{{answer.…}}`.
   */
  varCtx?: VariableContext;
  plans: PlanSelectorPlan[];
  layout: 'cards' | 'list';
  defaultPriceId?: string;
  stripePrices?: PublicStripePrice[];
  blockId: string;
  // WEB-622: editable placeholder for a plan whose Stripe price does not resolve.
  noPriceLabel?: string;
  /** WEB-925: id стиль-пака — способ выделения тарифа и приглушение соседей. */
  stylePackId?: string;
  /** WEB-1651-3: цвет отметки радио тарифа; пусто — палитра воронки. */
  optionAccentColor?: string;
  /**
   * WEB-1692 юнит 4 (ДЕНЬГИ). `title` — главная строка карточки (цена, а без
   * подгруженной цены название тарифа: это ОДИН узел разметки); `secondary` —
   * подпись-бейдж. Строки цены под главной считаются ОТ авторского кегля
   * прежними отношениями — продающие пропорции карточки не схлопываются
   * (вариант А, решение владельца 02.09).
   */
  textRoles?: { title: TextRoleStyle; secondary: TextRoleStyle };
}

/**
 * WEB-1692 юнит 4: отношения кеглей ВНУТРИ карточки тарифа — ровно те, что
 * стоят в разметке ниже (главная строка 20, период 12, настоящее списание 15 в
 * «заметном» режиме и 12 в обычном). Числа объявлены здесь один раз.
 */
const PLAN_UNIT_LABEL_RATIO = 12 / 20;
const PLAN_ACTUAL_PROMINENT_RATIO = 15 / 20;
const PLAN_ACTUAL_RATIO = 12 / 20;

function PlanSelectorBlock({
  plans,
  layout,
  defaultPriceId,
  stripePrices,
  blockId,
  noPriceLabel,
  stylePackId,
  locale,
  varCtx,
  optionAccentColor,
  textRoles = { title: {}, secondary: {} },
}: PlanSelectorBlockProps): React.ReactElement {
  const { values, setValue, setDefaultValue } = useFormValues();
  // WEB-786: выбор держим ИНДЕКСОМ, а не priceId. У AI-сгенерированного
  // пейволла priceId пуст у всех планов (владелец привязывает цены позже) —
  // сравнение по priceId делало «выбранными» сразу все карточки.
  // WEB-1714: начальная карточка — ОБЩИМ правилом умолчания; той же функцией
  // лестница оплаты берёт цену селектора у посетителя, обошедшего этот экран.
  // WEB-3335 доработка: при ПОВТОРНОМ показе (возврат «назад») — то, что уже
  // записано этим селектором, а не умолчание: выбор покупателя не сбрасывается.
  const [selectedIndex, setSelectedIndex] = useState<number>(() => {
    const stored = values[blockId];
    const restored = stored ? plans.findIndex((p) => p.priceId === stored) : -1;
    return restored >= 0
      ? restored
      : planSelectorDefaultIndex(plans, defaultPriceId);
  });
  const selectedId = plans[selectedIndex]?.priceId;

  // WEB-447 §4.3a (MONEY): the shown plan is recorded for checkout even when
  // the user never touches the selector. WEB-3335 доработка (решение владельца
  // 15.10, ночь): this is a DEFAULT write — it never overrides or outranks an
  // explicit choice (`setDefaultValue`); the user's pick goes through `choose`.
  // The setter is intentionally omitted from deps: its identity changes on
  // every form-value update (FormValuesContext memoises on `values`), and
  // depending on it would re-fire this effect in a loop.
  useEffect(() => {
    if (selectedId) setDefaultValue(blockId, selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, blockId]);

  // Явный выбор покупателя — в том числе нажатие на уже отмеченный
  // умолчанием тариф (радио при этом не шлёт change, поэтому и onClick).
  const choose = (index: number): void => {
    setSelectedIndex(index);
    const id = plans[index]?.priceId;
    if (id) setValue(blockId, id);
  };

  // WEB-925: способ выделения и приглушение соседей — решения о РАЗМЕТКЕ,
  // рендерер берёт их прямо из пака (сырой enum в CSS-переменной = мёртвое
  // поле, шрам shape.shadow). Пака нет → сегодняшний вид.
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  const highlight: PlanHighlight =
    pack?.paywall.planHighlight ?? 'chip-on-border';
  const dimUnselected = pack?.paywall.dimUnselectedPlans === true;
  const badgeStyle = planBadgeStyle(highlight);
  // WEB-925 атом 5: способ ПОДАЧИ цены. Только отображение — priceId, сумма
  // чекаута и Stripe не трогаются (правила и арифметика — derivePriceDisplay).
  const priceMode = pack?.paywall.priceMode;

  return (
    <div
      data-testid="plan-selector"
      data-plan-highlight={highlight}
      style={
        {
          // WEB-847: «Карточки» — сетка от ширины контейнера; «Список» остаётся
          // ровно тем, чем был (flex-колонка), его раскладку не трогаем.
          ...(layout === 'cards'
            ? {
                display: 'grid',
                // min(...,100%) — страховка от переполнения на контейнере уже
                // 150px: без неё трек остался бы 150px и вылез за край.
                gridTemplateColumns: `repeat(auto-fit, minmax(min(${PLAN_CARD_MIN_WIDTH}px, 100%), 1fr))`,
              }
            : {
                display: 'flex',
                flexDirection: 'column' as const,
                flexWrap: 'nowrap' as const,
              }),
          gap: PLAN_GRID_GAP,
          marginBottom: 16,
          ['--plan-accent' as string]: planAccentValue(stylePackId),
          ['--plan-accent-on' as string]: planAccentOnValue(stylePackId),
        } as React.CSSProperties
      }
    >
      {plans.map((plan, index) => {
        const price = findStripePrice(stripePrices, plan.priceId);
        const isSelected = index === selectedIndex;
        const hasBadge = hasRichText(asRichField(plan.badge));
        const priceDisplay = price
          ? derivePriceDisplay(price, priceMode, locale)
          : undefined;
        // Режим ничего не изменил (нет пака, делить не на что, округлилось в
        // ноль) → одна строка, ровно как вчера.
        // WEB-1014: акцент на пробном периоде ничего не делит и не даёт
        // подписи — он даёт вторую строку. Без `actual` в условии режим
        // проваливался бы в однострочный фолбэк и триал не показывался вовсе.
        const splitPrice =
          priceDisplay !== undefined &&
          (priceDisplay.recalculated ||
            priceDisplay.unitLabel !== '' ||
            priceDisplay.actual !== null);
        return (
          <label
            // Scale A4: key по index, НЕ по priceId — priceId редактируемый и
            // пуст/дублируется у новых планов; общий key '' у пустых планов ломал
            // React-reconciliation при выборе цены (фантомная карточка).
            key={index}
            data-testid={`plan-option-${plan.priceId}`}
            {...(isSelected ? { 'data-stripe-price': plan.priceId } : {})}
            // WEB-925: приглушение невыбранных — классом, а не инлайном:
            // непрозрачность задаёт --plan-unselected-opacity из пака, без
            // пака переменной нет → фолбэк 1 → вид прежний.
            {...(dimUnselected && !isSelected
              ? { className: 'funnel-plan-unselected' }
              : {})}
            style={{
              // WEB-847: ширину карточки задаёт трек сетки контейнера — здесь
              // больше нет ни flex-basis, ни константы «половина ряда»
              // (WEB-621 фиксировал 2-в-ряд на любой ширине, из-за чего превью
              // и реальное устройство расходились).
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '14px 16px',
              // WEB-925: карточке с крышкой нужен зазор сверху, иначе крышка
              // легла бы на соседнюю карточку.
              marginTop:
                highlight === 'cap-header' && hasBadge
                  ? PLAN_CAP_HEIGHT
                  : undefined,
              borderRadius: 'var(--border-radius-lg, 12px)',
              border: `2px solid ${plan.highlighted || isSelected ? PLAN_ACCENT : PLAN_CARD_BORDER}`,
              background: 'var(--color-bg-surface, #fff)',
              // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
              color:
                'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {/* WEB-809 wave 2a: badge — RichField; hasRichText вместо
                truthiness (пустой spans-массив truthy, но бейджа не должно быть). */}
            {hasBadge && (
              <span
                data-testid="plan-badge"
                // WEB-925: геометрию задаёт способ выделения из пака
                // (чип на рамке / крышка / чип на стыке). Без пака — как вчера.
                // WEB-1692 юнит 4: авторская накладка ПОСЛЕ — пустая ничего не
                // меняет, а геометрия чипа остаётся за паком.
                style={{ ...badgeStyle, ...textRoles.secondary }}
              >
                <RichTextContent
                  value={asRichField(plan.badge)}
                  varCtx={varCtx}
                />
              </span>
            )}
            <input
              type="radio"
              name={blockId}
              value={plan.priceId}
              checked={isSelected}
              onChange={() => choose(index)}
              onClick={isSelected ? () => choose(index) : undefined}
              // WEB-1651-3: радио выбора тарифа — та же отметка, что у
              // вариантов ответа (одна логика на оба рендерера).
              style={{
                width: 18,
                height: 18,
                accentColor: resolveOptionAccent(optionAccentColor),
              }}
            />
            <span
              data-testid="plan-price"
              style={{
                fontSize: price ? 20 : 14,
                fontWeight: price ? 700 : 400,
                // WEB-966: карточка тарифа стоит на СВОЕЙ светлой плашке
                // (--color-bg-surface выше по этому же стилю), поэтому её текст
                // обязан идти через --color-text-on-surface. Через
                // --color-text-primary нельзя: на экране с фоновой картинкой
                // обёртка фона ПИННИТ её белой ради текста поверх вуали
                // (WEB-872) — и цена становилась белой на белом (замер QA
                // 02.08: контраст 1:1, карточки тарифов без цен).
                // Фолбэк одноуровневый: вложенный var() в инлайне jsdom
                // отбрасывает целиком (шрам editor.md), правило стало бы
                // непроверяемым.
                color: 'var(--color-text-on-surface, #1a1a1a)',
                // WEB-1692 юнит 4: главная строка карточки тарифа. «Размер
                // текста внутри элемента» сюда не доезжал никогда — у каждого
                // текста тарифа свой явный размер, а явный сильнее
                // унаследованного (корень тот же, что в WEB-1673).
                ...textRoles.title,
              }}
            >
              {/* показываем цену; если не подтянулась — нейтральный плейсхолдер
                 (бейдж уже есть тегом сверху), но НИКОГДА не сырой priceId.
                 WEB-809 wave 2a: per-plan noPriceLabel — RichField; выбор
                 фолбэка по hasRichText (пустые spans = пустая строка). */}
              {price && priceDisplay && splitPrice ? (
                /* WEB-925 атом 5: пересчитанная/переподанная цена крупно, а под
                   ней — период и НАСТОЯЩЕЕ списание той же строкой, что видна
                   без пака. Покупатель не должен решить, что спишут «за день». */
                <span
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    lineHeight: 1.2,
                  }}
                >
                  <span data-testid="plan-price-main">{priceDisplay.main}</span>
                  {priceDisplay.unitLabel !== '' && (
                    <span
                      data-testid="plan-price-unit"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        // WEB-966: та же светлая плашка, что и у цены.
                        // --color-text-secondary тоже пиннится вуалью
                        // (rgba(255,255,255,.82)) и исчезала бы вместе с ней.
                        // Иерархию держат кегль и насыщенность, а не цвет.
                        color: 'var(--color-text-on-surface, #595959)',
                        // Период считается ОТ кегля главной строки (вариант А).
                        ...scaledTextRoleStyle(
                          textRoles.title,
                          PLAN_UNIT_LABEL_RATIO,
                        ),
                      }}
                    >
                      {priceDisplay.unitLabel}
                    </span>
                  )}
                  {priceDisplay.actual !== null && (
                    <span
                      data-testid="plan-price-actual"
                      style={{
                        // WEB-1014 п.3: при акценте на пробном периоде цена
                        // после него — не мелкий шрифт под крупным «бесплатно»,
                        // а полноценная вторая строка. В режимах пересчёта
                        // («в день»/«в месяц») строка списания остаётся
                        // подписью — там крупно подан САМ пересчёт цены.
                        fontSize: priceDisplay.actualProminent ? 15 : 12,
                        fontWeight: priceDisplay.actualProminent ? 600 : 400,
                        // WEB-966: настоящее списание — самая важная строка
                        // честности подачи цены (WEB-925 атом 5). Исчезнуть
                        // вместе с палитрой страницы она права не имеет.
                        color: 'var(--color-text-on-surface, #595959)',
                        // Настоящее списание — тоже от кегля главной строки, и
                        // РАЗНЫМ отношением по режиму: «заметная» строка
                        // остаётся крупнее обычной (WEB-1014 п.3).
                        ...scaledTextRoleStyle(
                          textRoles.title,
                          priceDisplay.actualProminent
                            ? PLAN_ACTUAL_PROMINENT_RATIO
                            : PLAN_ACTUAL_RATIO,
                        ),
                      }}
                    >
                      {priceDisplay.actual}
                    </span>
                  )}
                </span>
              ) : price ? (
                formatStripePrice(price)
              ) : (
                <RichTextContent
                  value={asRichField(
                    hasRichText(asRichField(plan.noPriceLabel))
                      ? plan.noPriceLabel
                      : noPriceLabel,
                  )}
                  varCtx={varCtx}
                  fallback={getPublicQuizStrings(locale).planNoPriceFallback}
                />
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Те же общие помощники, что у остального рендерера: подстановка переменных,
 * богатый текст, цвет содержимого. Они повторяют преамбулу
 * `PublicBlockRendererExtended` НЕ по лени — ветки ниже переехали вместе со
 * своим обиходом, и вынести преамбулу в общий хук значило бы тронуть каждую из
 * полусотни оставшихся веток. Порядок и смысл строк совпадают до байта.
 */
export function PublicBlockRendererCommon({
  block,
  textColor,
  varCtx,
  stripePrices,
  buttonStyle,
  stylePackId,
  screenBlocks,
  funnelBlocks,
  hiddenBlockIds,
  followsExplicitSpacing,
}: PublicExtendedBlockProps): React.ReactElement | null {
  const { isEditorPreview } = useEditorPreview();
  const contentTextColor = resolveContentTextColor(block, textColor);
  const interp = (text: string | undefined): string =>
    text == null ? '' : varCtx ? interpolate(text, varCtx) : text;
  const richText = (value: string | undefined, fallback?: React.ReactNode) => (
    <RichTextContent
      value={asRichField(value)}
      varCtx={varCtx}
      fallback={fallback}
    />
  );
  const hasText = (value: string | undefined): boolean =>
    hasRichText(asRichField(value));
  const { values } = useFormValues();
  // Резерв места под обложку карточки — тот же хук и то же место (до switch),
  // что в соседнем рендерере: правила хуков запрещают звать его из ветки.
  const cardCoverImage = useImageSpaceReserve(
    { width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' },
    block.cardImageUrl,
  );

  const rendered = ((): React.ReactElement | null => {
    switch (block.type) {
      case 'plan_selector': {
        const ai = aiFields(block);
        // Scale A4: единый резолвер (панель/детектор/рендер один список планов).
        const plans = resolvePlanSelectorPlans(block);
        const layout = block.planSelectorLayout ?? ai.layout ?? 'cards';
        const defaultPriceId =
          block.planSelectorDefaultPriceId ?? ai.defaultPriceId;
        if (plans.length === 0) return null;
        return (
          <PlanSelectorBlock
            plans={plans}
            layout={layout}
            defaultPriceId={defaultPriceId}
            stripePrices={stripePrices}
            blockId={block.id}
            noPriceLabel={block.planSelectorNoPriceLabel}
            stylePackId={stylePackId}
            locale={varCtx?.system?.locale}
            varCtx={varCtx}
            optionAccentColor={block.optionAccentColor}
            textRoles={blockTextRoles(block, 'plan_selector')}
          />
        );
      }

      case 'payment_terms': {
        const ai = aiFields(block);
        const authoredTemplate = block.paymentTermsTemplate ?? ai.template;
        if (!authoredTemplate) return null;
        // WEB-1905 (ревизия пачки 29): старый английский шаблон конструктора
        // до 05.09 в содержимом = незаданный → словарь по языку воронки.
        const template = resolvePaywallText(
          authoredTemplate,
          getPublicQuizStrings(varCtx?.system?.locale).blockDefaults
            .paymentTermsTemplate,
        );
        const staticPriceId = block.paymentTermsPriceId ?? ai.priceId;
        // WEB-936 п.9: тариф в тексте условий обязан следовать за ЖИВЫМ выбором
        // покупателя, а не всегда показывать статичный staticPriceId. Живой
        // выбор мирроит PlanSelectorBlock в общие form-values по СВОЕМУ id
        // (тот же контракт, что уже читает resolvePriceId для кнопки оплаты) —
        // найти его id можно только среди блоков экрана/воронки, которые
        // рендерер сам не знает (получает только СВОЙ block), поэтому
        // вызывающий передаёт их необязательными screenBlocks/funnelBlocks.
        // WEB-1302 (хвост) + решение владельца Р2: лестница поиска — ПОЛНАЯ
        // лестница кнопки оплаты (resolvePaymentTermsPriceId: живой выбор
        // тарифа — текущий экран, потом вся воронка, скрытый правилами
        // селектор игнорируется — затем своя цена платёжной кнопки этого
        // экрана, затем первый ВИДИМЫЙ блок с ценой на экране) —
        // текст обязан обещать ту же цену, что спишет кнопка, включая выбор
        // тарифа с ПРЕДЫДУЩЕГО экрана. Раньше без живого выбора текст падал
        // сразу на ручное поле staticPriceId, которое ни с чем не сверяется,
        // а кнопка списывала цену первого видимого блока с ценой: текст
        // обещал одну сумму, списывалась другая. Оговорка WEB-1113 про
        // скрытые блоки чужих экранов наследуется осознанно — см. комментарий
        // у хелпера. Ручное поле — ПОСЛЕДНИЙ запасной вариант: селектора и
        // блока с ценой нет нигде, выбора ещё нет, или найденная цена не
        // резолвится в stripePrices (иначе юр-текст ушёл бы дырой вместо
        // честного скрытия).
        // РЕШЕНИЕ ВЛАДЕЛЬЦА Р2 (24.08, WEB-1302): прежнее расхождение
        // закрыто. Вторая ступень лестницы кнопки — её собственное
        // `block.stripePriceId`; у блока условий такого поля нет (ручное —
        // paymentTermsPriceId, ПОСЛЕ лестницы), поэтому текст проваливался
        // сразу на «первый видимый блок с ценой» и обещал цену соседнего
        // блока, пока кнопка списывала свою. Правда — цена кнопки: ступень
        // «своя цена платёжной кнопки этого экрана» встала второй позицией
        // внутри resolvePaymentTermsPriceId, лестницы текста и кнопки
        // совпадают целиком. Подробнее — комментарий в resolvePriceId.ts.
        const ladderPriceId = resolvePaymentTermsPriceId(
          screenBlocks ?? [],
          values,
          funnelBlocks,
          hiddenBlockIds,
        );
        const ladderPrice = ladderPriceId
          ? findStripePrice(stripePrices, ladderPriceId)
          : undefined;
        const price =
          ladderPrice ?? findStripePrice(stripePrices, staticPriceId);
        // WEB-936 п.1: денежная подстановка ПЕРВОЙ. F6-резолвер переменных
        // плеера fail-open — он стирает любой неизвестный ему {{…}} в пустую
        // строку, а {{price}}/{{trial_days}}/{{renewal_price}} ему неизвестны.
        // Прежний порядок (interp → деньги) выпотрошивал шаблон на публике, где
        // varCtx есть; в редакторе varCtx нет, поэтому баг был виден только на бою.
        const priced = interpolatePaymentTerms(template, price);
        if (priced === null) return null;
        const resolvedText = interp(priced);
        return (
          <p
            data-testid="payment-terms-text"
            // WEB-807: стили из настроек, а не хардкод — паритет с текстовыми
            // блоками (heading/paragraph): textColor пейволла + block.size/align.
            style={{
              // Запрос владельца продукта 10.08: точный px (block.fontSize)
              // сильнее пресета size — absent оставляет прежний маппинг.
              fontSize:
                block.fontSize ??
                (block.size === 'sm' ? 12 : block.size === 'lg' ? 15 : 13),
              // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
              color: contentTextColor ?? 'var(--color-text-secondary, #8c8c8c)',
              textAlign: block.align ?? 'center',
              marginBottom: 16,
            }}
          >
            {resolvedText}
          </p>
        );
      }

      /**
       * WEB-1822 — «Цена по частям»: приём «Price in days, not dollars».
       * Два предложения автора вокруг двух сумм: цена выбранного тарифа и она
       * же в пересчёте на день.
       *
       * ЦЕНА — ИЗ ОДНОГО ИСТОЧНИКА С КНОПКОЙ ОПЛАТЫ. Своего priceId у блока
       * нет намеренно: он идёт ТОЙ ЖЕ лестницей, что юридический текст
       * условий (resolvePaymentTermsPriceId — живой выбор «Выбора тарифа» на
       * этом экране, затем кросс-экранно, затем своя цена платёжной кнопки
       * экрана, затем первый видимый блок с ценой; решение владельца Р2 от
       * 24.08, WEB-1302). Обещать «всего $0.21 в день» про тариф, который
       * покупатель не выбирал, — та же ложь, что расхождение текста условий
       * со списанием. В редакторе живого выбора нет — там срабатывают нижние
       * ступени той же лестницы, второго «превью-тарифа» не заводим.
       *
       * АРИФМЕТИКА — ОДНА НА ЭКРАН. Пересчёт «в день» считает
       * `perDayMinorUnits` (derivePriceDisplay.ts): те же cycleDays
       * (год=365, месяц=365/12, неделя=7, кратные периоды — по числу дней),
       * то же округление half-up и то же правило «ноль после округления не
       * показываем». Карточка просила «месяц→/30» — канон кода 365/12
       * оставлен НАМЕРЕННО: иначе годовой и месячный тарифы одного продукта
       * давали бы на соседних блоках разную цену дня.
       *
       * Пустые поля просто выпадают из склейки: пустой текст ДО = сумма
       * начинает предложение. Цены нет вовсе или цикла нет (разовый платёж) —
       * соответствующей строки нет совсем: «$0.00 в день» и «Всего в день»
       * покупателю показывать нельзя.
       *
       * Своих слов у ветки нет ни одного: весь видимый текст пишет автор на
       * языке воронки, а единственное подставляемое слово — период тарифа по
       * плейсхолдеру {{period}} (WEB-1826) — берётся из словаря по языку
       * воронки, не из литерала.
       */
      case 'price_breakdown_text': {
        const ladderPriceId = resolvePaymentTermsPriceId(
          screenBlocks ?? [],
          values,
          funnelBlocks,
          hiddenBlockIds,
        );
        const ladderPrice = ladderPriceId
          ? findStripePrice(stripePrices, ladderPriceId)
          : undefined;
        // ПРАВИЛО 7 (isUsableAmount, derivePriceDisplay): непригодная сумма —
        // это НЕ цена, а её отсутствие. Нулевая/дробная/отрицательная цена
        // приезжает из каталога живьём (сорванная синхронизация со Stripe,
        // черновик тарифа), и без этой отсечки покупатель читал «Всего $0.00
        // в год» — обещание бесплатности там, где цены просто нет. Строка «в
        // день» это правило соблюдала с начала (perDayMinorUnits), главная
        // сумма — нет.
        const price =
          ladderPrice && isUsableAmount(ladderPrice.unitAmount)
            ? ladderPrice
            : undefined;

        const breakdownStyle: React.CSSProperties = {
          // Кегль — как у соседнего блока условий: точный px сильнее
          // пресета size, absent оставляет прежний маппинг.
          fontSize:
            block.fontSize ??
            (block.size === 'sm' ? 14 : block.size === 'lg' ? 20 : 16),
          // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
          color: contentTextColor ?? 'var(--color-text-primary, inherit)',
          // WEB-1822 (приёмка 17.09), дефект 1: положение ставим ЗДЕСЬ, как у
          // соседа payment_terms выше. Прежде ветка полагалась только на общую
          // обёртку WEB-1695 — но обёртка при `align === undefined` не
          // оборачивает вовсе (resolveBlockAlign отдаёт undefined), а панель
          // показывает автору умолчание defaultAlignOf = 'center'. Итог был:
          // в панели «по центру», на экране прижато влево, пока автор не
          // тронет переключатель. Умолчание общей обёртки не трогаем — она
          // общая на три десятка видов, и сдвинула бы чужие блоки.
          // Двух старшинств тут нет: и обёртка (режим 'content' — тот же
          // textAlign), и эта строка берут одно и то же block.align.
          textAlign: block.align ?? 'center',
          marginBottom: 16,
        };

        if (!price) {
          // НА БОЮ — молчание: своей строки покупателю мы не показываем ни на
          // каком языке, а обещать цену, которой нет, нельзя.
          if (!isEditorPreview) return null;
          // В ХОЛСТЕ РЕДАКТОРА блок обязан быть виден и выделяем, иначе автор
          // добавил его и не видит ничего (класс «блок-невидимка»). Строка
          // служебная, покупателю недостижима — тот же приём, что у прочих
          // подсказок превью (шрам WEB-846).
          return (
            <div
              data-testid="price-breakdown-empty"
              style={{ ...breakdownStyle, opacity: 0.6 }}
            >
              {pickStrings(authorHintsStrings).priceAppearsWithPlan}
            </div>
          );
        }

        /**
         * WEB-1826: слова о периоде тоже следуют за ВЫБРАННЫМ тарифом.
         *
         * Сумму блок подставлял живьём, а «в год» автор писал статикой — на
         * экране с выбором тарифов покупатель месячного плана читал «Всего
         * $9.99 в год»: сумма честная, подпись врёт. Автор пишет {{period}}
         * (приём шаблона payment_terms с его {{price}}), рендер подставляет
         * слово периода на языке воронки. Текст БЕЗ плейсхолдера проходит
         * побайтно — прежние воронки не меняются (см. substitutePeriodPlaceholder).
         *
         * Своих слов у ветки по-прежнему нет: и «в месяц», и «a month» берутся
         * из словаря publicQuizStrings по языку воронки (WEB-1821).
         */
        const periodWord = pricePeriodWord(
          price,
          getPublicQuizStrings(varCtx?.system?.locale),
        );

        /**
         * ПРАВИЛО ПУНКТУАЦИИ — ОДНО НА ОБА ПУТИ (WEB-1822, приёмка 17.09,
         * дефект 2). Блок рисуется двумя путями (обычная склейка и кусковая,
         * когда у текста задан свой цвет или кегль), и пробел перед знаком
         * препинания зависел от того, какой путь включился: кусковый путь
         * знак препинания учитывал, обычная склейка лепила пробел безусловно —
         * «Всего $9.99 , платёж один» против «Всего $9.99, платёж один» на
         * одной и той же настройке. Теперь правило живёт ОДНО, здесь, и через
         * него идут оба пути.
         */
        const startsWithPunctuation = (text: string): boolean =>
          /^[,.;:!?)»%]/.test(text);
        const joinPieces = (parts: string[]): string =>
          parts.reduce(
            (line, part) =>
              !line
                ? part
                : startsWithPunctuation(part)
                  ? line + part
                  : `${line} ${part}`,
            '',
          );

        /**
         * ТЕКСТ АВТОРА → ЭКРАН: период ПЕРВЫМ, переменные воронки ПОСЛЕ.
         *
         * Порядок закреплён тестом и не переставляется (шрам WEB-936,
         * payment_terms): резолвер переменных плеера fail-open — незнакомое
         * ему имя он стирает в пустую строку, а {{period}} (WEB-1826) ему
         * незнакомо. Обратный порядок молча съел бы слово периода на бою, где
         * varCtx есть, и остался бы невидимым в редакторе, где varCtx нет.
         *
         * ДЕФЕКТ 3 (приёмка 17.09): переменные воронки тут не подставлялись
         * вовсе, хотя схема объявляет блок близнецом блока условий оплаты, а
         * сосед по пачке их подставляет — автор писал «это {{query.goal}} в
         * день», и фигурные скобки уезжали покупателю на экран оплаты.
         *
         * Подстановка идёт ПО КУСКАМ, а пустой кусок выпадает из склейки уже
         * ПОСЛЕ неё: «{{period}}, платёж один» на разовом платеже даёт
         * «, платёж один», и пробел перед запятой снимает правило пунктуации
         * выше — тот самый случай, ради которого WEB-1826 подставлял в
         * собранное предложение. Текст без плейсхолдеров проходит побайтно:
         * подстановки возвращают его тем же значением — прежние воронки не
         * меняются по построению.
         */
        const authorText = (text: string | undefined): string =>
          interp(
            substitutePeriodPlaceholder(text?.trim() ?? '', periodWord),
          ).trim();

        const sentence = (
          before: string | undefined,
          amount: string,
          after: string | undefined,
        ): string =>
          joinPieces(
            [authorText(before), amount, authorText(after)].filter(
              (part): part is string => !!part,
            ),
          );

        /**
         * Заказ владельца 01.09: свои цвет и кегль у каждого из четырёх
         * текстов. Кусковый режим включается ПО-СТРОЧНО и только когда у
         * строки задан хоть один кусковый стиль: строка без стилей идёт
         * прежним путём (sentence на собранном предложении) — прежние воронки
         * не меняются ни на байт по построению. Сумму кусковые стили не
         * красят никогда (деньги на экране, WEB-925).
         *
         * {{period}} в кусковом режиме подставляется в КАЖДОМ куске той же
         * substitutePeriodPlaceholder; разницу склейки (пробел перед
         * плейсхолдером на границе кусков) закрывает правило пунктуации:
         * перед куском, начинающимся со знака препинания, пробел не ставится
         * — ровно те случаи, ради которых WEB-1826 подставлял в собранное
         * предложение.
         */
        const styledLine = (
          lineKey: 'Price' | 'Daily',
          before: string | undefined,
          amount: string,
          after: string | undefined,
        ): React.ReactNode => {
          const pieces = [
            {
              part: `before${lineKey}`,
              text: before,
              color:
                lineKey === 'Price'
                  ? block.beforePriceTextColor
                  : block.beforeDailyTextColor,
              fontSize:
                lineKey === 'Price'
                  ? block.beforePriceTextFontSize
                  : block.beforeDailyTextFontSize,
            },
            { part: `amount-${lineKey.toLowerCase()}`, text: amount },
            {
              part: `after${lineKey}`,
              text: after,
              color:
                lineKey === 'Price'
                  ? block.afterPriceTextColor
                  : block.afterDailyTextColor,
              fontSize:
                lineKey === 'Price'
                  ? block.afterPriceTextFontSize
                  : block.afterDailyTextFontSize,
            },
          ] as Array<{
            part: string;
            text?: string;
            color?: string;
            fontSize?: number;
          }>;

          const hasPieceStyle = pieces.some(
            (p) => p.color !== undefined || p.fontSize !== undefined,
          );
          if (!hasPieceStyle) return sentence(before, amount, after);

          const out: React.ReactNode[] = [];
          for (const p of pieces) {
            const isAmount = p.part.startsWith('amount');
            // Тот же порядок подстановок и та же проверка пустоты ПОСЛЕ них,
            // что на обычном пути: куски идут через общий authorText.
            const text = isAmount ? (p.text ?? '') : authorText(p.text);
            if (!text) continue;
            if (out.length > 0 && !startsWithPunctuation(text)) out.push(' ');
            out.push(
              <span
                key={p.part}
                data-testid={isAmount ? `pb-${p.part}` : `pb-part-${p.part}`}
                style={{
                  ...(p.color ? { color: p.color } : {}),
                  ...(p.fontSize ? { fontSize: p.fontSize } : {}),
                }}
              >
                {text}
              </span>,
            );
          }
          return out;
        };

        const perDay = perDayMinorUnits(price);

        return (
          <div data-testid="price-breakdown-text" style={breakdownStyle}>
            <div data-testid="price-breakdown-price">
              {styledLine(
                'Price',
                block.beforePriceText,
                formatPriceAmount(price.unitAmount, price.currency),
                block.afterPriceText,
              )}
            </div>
            {perDay !== null && (
              <div data-testid="price-breakdown-daily">
                {styledLine(
                  'Daily',
                  block.beforeDailyText,
                  formatPriceAmount(perDay, price.currency),
                  block.afterDailyText,
                )}
              </div>
            )}
          </div>
        );
      }

      case 'hero': {
        const align = block.align ?? 'center';
        const textAlign = align as 'left' | 'center' | 'right';
        // WEB-875 (живой прогон Somatica): белый текст и тёмный скрим уместны
        // ТОЛЬКО над реальной фото-подложкой. Без imageUrl (слот картинки не
        // выжил / не назначен) hero рендерился как белый хардкод поверх скрима
        // на СВЕТЛОЙ поверхности темы — заголовок тонул. Без фото: цвета темы,
        // без скрима.
        const hasHeroMedia = Boolean(block.imageUrl);
        // WEB-997: без медиа hero стоит на плашке-ПОВЕРХНОСТИ — его текст обязан
        // идти через --color-text-on-surface (класс WEB-966), а не через
        // --color-text-primary: ту обёртка фул-скрин фона ПИННИТ белой ради
        // читаемости над вуалью (WEB-872), и на светлой плашке текст исчезал —
        // «пустой» первый экран пейволла (живое репро PM 2026-08-03).
        // Фолбэк одноуровневый: вложенный var() в инлайне jsdom отбрасывает
        // целиком (класс WEB-925) — правило стало бы непроверяемым.
        const heroTitleColor = hasHeroMedia
          ? '#ffffff'
          : 'var(--color-text-on-surface, #1a1a1a)';
        const heroSubColor = hasHeroMedia
          ? 'rgba(255,255,255,0.85)'
          : 'var(--color-text-on-surface, #595959)';
        // WEB-1692 юнит 8: роли текста обложки. Умолчания выше НЕ трогаем —
        // они зависят от наличия фото (шрам WEB-875/997), и авторская
        // настройка только НАКЛАДЫВАЕТСЯ поверх: пусто = сегодняшний вид.
        const heroRoles = blockTextRoles(block, 'hero');
        return (
          <div
            style={{
              position: 'relative',
              width: '100%',
              minHeight: 260,
              borderRadius: 'var(--border-radius-lg, 16px)',
              overflow: 'hidden',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            {/* Background image layer */}
            {block.imageUrl ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${String(block.imageUrl)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'var(--color-bg-surface, #1e293b)',
                  // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
                  color:
                    'var(--color-text-on-surface, var(--color-text-primary, inherit))',
                }}
              />
            )}

            {/* Gradient scrim for text legibility — только над реальным фото */}
            {hasHeroMedia && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.24) 60%, rgba(0,0,0,0) 100%)',
                }}
              />
            )}

            {/* Content overlay */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                padding: '24px 20px',
                textAlign,
              }}
            >
              {hasText(block.title) && (
                <h1
                  style={{
                    // WEB-889 п.1: шкала от длины текста вместо разгона до 64px
                    // на десктопе (находка живой генерации PM 2026-07-29).
                    // WEB-1692 юнит 8: заданный автором кегль шкалу ОТМЕНЯЕТ —
                    // иначе настройка была бы мёртвой (clamp сильнее числа,
                    // потому что стоит в том же свойстве последним). Про это
                    // сказано в подсказке панели.
                    fontSize:
                      heroRoles.title.fontSize ??
                      (String(block.title).length > 28
                        ? 'clamp(28px, calc(var(--vp-w, 100vw) * 0.05), 44px)'
                        : 'clamp(32px, calc(var(--vp-w, 100vw) * 0.06), 48px)'),
                    fontWeight: 800,
                    color: heroRoles.title.color ?? heroTitleColor,
                    margin: '0 0 8px',
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {richText(block.title)}
                </h1>
              )}
              {hasText(block.subtitle) && (
                <p
                  style={{
                    // WEB-1692 юнит 8: вторая роль обложки — подзаголовок.
                    fontSize: heroRoles.secondary.fontSize ?? 16,
                    color: heroRoles.secondary.color ?? heroSubColor,
                    margin: '0 0 16px',
                    lineHeight: 1.4,
                  }}
                >
                  {richText(block.subtitle)}
                </p>
              )}
              {hasText(block.ctaText) && (
                <button
                  // WEB-447 wave1 slice2: hero CTA delegates to the shared
                  // primaryButtonStyle util (buttonStyle-aware; defaults to pill).
                  style={{
                    display: 'inline-block',
                    ...primaryButtonStyle(buttonStyle),
                    // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон
                    // кнопки» и цвет текста из панели — перекрывают акцент
                    // темы ТОЛЬКО когда заданы.
                    //
                    // WEB-1885: `innerBackground` сюда НЕ передаётся — то же
                    // решение и по той же причине, что у кнопки «Баннера
                    // срочности» (WEB-1711, ветка urgency_banner выше). У
                    // обложки «элемент» — сама карточка: её поверхность красит
                    // ПЕРЕМЕННАЯ `--color-bg-surface`, которую публикует
                    // обёртка blockInnerSurface. Общий сборщик кнопок при
                    // пустом «Фоне кнопки» откатывается на «Заливку элемента»
                    // — и кнопка получала ровно цвет подложки, на которой
                    // стоит, то есть сливалась с ней (находка двустороннего
                    // сторожа selfPaintedInnerFillEnds). Заливка кнопки теперь
                    // задаётся ТОЛЬКО своим полем `buttonBackground` («Фон
                    // кнопки», секция панели у hero есть с 10.08); пусто =
                    // акцент темы.
                    //
                    // РЕВИЗИЯ ПАЧКИ 9 (Ф10): прежняя приписка «вид старых
                    // воронок не меняется» была неправдой и её сняли. У
                    // обложки с ЗАДАННОЙ «Заливкой элемента» и пустым «Фоном
                    // кнопки» кнопка раньше красилась заливкой карточки, а
                    // теперь берёт акцент темы — то есть цвет меняется, и
                    // ровно в этом смысл правки: там, где кнопка сливалась с
                    // подложкой, она снова видна. Не меняется вид у обложек
                    // без заданной заливки (их кнопка и раньше брала акцент)
                    // и у тех, где «Фон кнопки» задан явно.
                    //
                    // Реестр SELF_PAINTED_INNER_FILL_TYPES тут не подошёл бы:
                    // он отбирает переменную у ВСЕГО поддерева, то есть отнял
                    // бы заливку у самой обложки. Мост рвётся в источнике.
                    //
                    // Цвет надписи (`innerTextColor`) кнопке отдаётся
                    // по-прежнему: это второй читатель поля, на котором стоит
                    // вердикт «живая всегда» у обложки (WEB-1696/1730) — с
                    // фото её заголовок жёстко белый, и других читателей нет.
                    // Поля рамки — тоже: обложка в BUTTON_SURFACE_BORDER_TYPES
                    // как «единственная кнопка блока» (WEB-1823).
                    ...buttonSurfaceStyle({
                      buttonBackground: block.buttonBackground,
                      innerTextColor: block.innerTextColor,
                      borderWidth: block.borderWidth,
                      borderColor: block.borderColor,
                      borderRadius: block.borderRadius,
                    }),
                  }}
                >
                  {richText(block.ctaText)}
                </button>
              )}
            </div>
          </div>
        );
      }

      case 'bullet_list':
        return (
          <ul
            style={{
              marginBottom: 16,
              paddingLeft: 24,
              color: 'var(--color-text-primary, inherit)',
            }}
          >
            {(block.listItems ?? []).map((item, i) => (
              <li
                key={i}
                style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 4 }}
              >
                {/* WEB-809 wave 2a: пункт — RichField; строки рендерятся байт-в-байт. */}
                {richText(item)}
              </li>
            ))}
          </ul>
        );

      case 'icon_with_text':
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16,
              color: 'var(--color-text-primary, inherit)',
            }}
          >
            <span style={{ fontSize: 24 }}>{getIconEmoji(block.iconName)}</span>
            {/* Дозаказ владельца продукта: px-размер текста, не иконки-эмодзи. */}
            <span style={{ fontSize: block.fontSize ?? 16 }}>
              {richText(block.content)}
            </span>
          </div>
        );

      case 'card': {
        // WEB-872 п.1: the glass card used to hardcode white text
        // unconditionally — white-on-white whenever the card sits on a light
        // surface with nothing dark behind it (manual editor use, older
        // content the post-gen contrast guard never touched). It now falls
        // back to the theme's own readable text color; ScreenBackgroundWrapper
        // (WEB-872 п.3) pins --color-text-primary/--color-text-secondary to
        // light values on its content wrapper only while a real dark scrim is
        // rendered over a background image, so a glass card genuinely on a
        // dark scrim still reads light without this component needing to know
        // about screen backgrounds at all.
        // WEB-3243: картинка фоном под текстом (`cardImageMode`, помощники —
        // cardImageBackdrop.tsx). Без картинки и при `top` — прежний вид;
        // «стекло» при фоне не действует: подложкой служит картинка.
        const asBackdrop = isCardImageBackdrop(block);
        const isGlass = !asBackdrop && block.cardVariant === 'glass';
        const cardStyle: React.CSSProperties = isGlass
          ? {
              // WEB-1697: рамка блока — из справочника умолчаний
              // (blockBorder.ts). У «стеклянного» вида умолчание своё —
              // полупрозрачная белая рамка, как было.
              ...resolveBlockBorder(block),
              overflow: 'hidden',
              marginBottom: 16,
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              color: 'var(--color-text-primary, #1a1a1a)',
            }
          : {
              // WEB-1697: то же умолчание из справочника, вид «сплошная».
              ...resolveBlockBorder(block),
              overflow: 'hidden',
              marginBottom: 16,
              background: 'var(--color-bg-surface, transparent)',
              // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
              color:
                'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              boxShadow: 'var(--card-shadow, none)',
            };

        const descColor = asBackdrop
          ? 'rgba(255,255,255,0.85)'
          : 'var(--color-text-secondary, #595959)';

        // WEB-1692: заголовок карточки отдельно от описания (репорт владельца
        // 24.08: «также размер шрифта для заголовка и для Description»).
        // Накладка спредится ПОСЛЕ прежних строк и при пустых полях равна
        // пустому объекту — умолчание обоих видов, включая «стеклянный»
        // (шрам WEB-872), остаётся дословным.
        const cardRoles = blockTextRoles(block, 'card');

        return (
          <div
            style={
              asBackdrop ? cardBackdropShellStyle(cardStyle, block) : cardStyle
            }
          >
            {asBackdrop && <CardImageBackdrop src={block.cardImageUrl!} />}
            {!asBackdrop && block.cardImageUrl && (
              <img
                src={block.cardImageUrl}
                alt=""
                decoding="async"
                // WEB-1994: место под обложку карточки до её загрузки; онError
                // снимает резерв (починка ревизии, useImageSpaceReserve).
                style={cardCoverImage.style}
                onError={cardCoverImage.onError}
              />
            )}
            <div
              style={{
                padding: '16px 20px',
                ...(asBackdrop && { position: 'relative' }),
              }}
            >
              {/* WEB-1692 (ревизия пачки 6, находка 3.5): метка теста несёт
                  id блока — тот же образец, что у соседних `col2-*-${block.id}`.
                  Глобальная метка на всю страницу находит ПЕРВЫЙ такой блок:
                  на экране с двумя карточками проверка молча смотрела бы не на
                  тот блок и оставалась бы зелёной, что бы ни случилось со
                  вторым. */}
              <h3
                data-testid={`card-title-${block.id}`}
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 8,
                  marginTop: 0,
                  ...(asBackdrop && { color: '#fff' }),
                  ...cardRoles.title,
                }}
              >
                {richText(block.cardTitle)}
              </h3>
              <p
                data-testid={`card-description-${block.id}`}
                style={{
                  fontSize: 15,
                  color: descColor,
                  lineHeight: 1.6,
                  margin: 0,
                  ...cardRoles.secondary,
                }}
              >
                {richText(block.cardDescription)}
              </p>
            </div>
          </div>
        );
      }

      case 'columns_2': {
        // WEB-687: заголовок + контент + выравнивание per-колонка; без новых
        // полей — прежний вид (только контент).
        //
        // WEB-1692: ДВЕ РОЛИ ТЕКСТА — заголовки ОБЕИХ колонок отдельно от
        // содержимого ОБЕИХ. Группировка по роли, а не по колонке, — прямое
        // уточнение владельца: «не по колонкам, а по ролям».
        //
        // ПОЧЕМУ РОЛЬ СОДЕРЖИМОГО ЛОЖИТСЯ НА СВОЮ ОБЁРТКУ, А НЕ НА КОЛОНКУ:
        // кегль 15px стоит на самой колонке, и заголовок его НАСЛЕДУЕТ.
        // Положив роль содержимого туда же, мы двигали бы заодно и заголовок —
        // то есть ровно ту болезнь, которую карточка чинит. Обёртка появляется
        // ТОЛЬКО когда автор что-то задал: пусто = прежняя разметка байт в байт.
        const roles = blockTextRoles(block, 'columns_2');
        const columnBody = (
          side: 'left' | 'right',
          value: typeof block.column1Content,
        ) =>
          isRoleStyled(roles.secondary) ? (
            <div
              data-testid={`col2-content-${side}-${block.id}`}
              style={roles.secondary}
            >
              {richText(value)}
            </div>
          ) : (
            richText(value)
          );
        // WEB-2313: на телефоне колонки встают друг под другом. Без
        // переноса флекс не складывается никогда — на экране 390px каждой
        // колонке доставалось ~180px. Опорная ширина 240px: рядом колонки
        // стоят, только когда обёртка шире 496px (240 + 16 + 240).
        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              marginBottom: 16,
              color: 'var(--color-text-primary, inherit)',
            }}
          >
            <div
              data-testid={`col2-left-${block.id}`}
              style={{
                flex: '1 1 240px',
                fontSize: 15,
                ...(block.column1Align
                  ? { textAlign: block.column1Align }
                  : {}),
              }}
            >
              {hasText(block.column1Title) && (
                <div
                  data-testid={`col2-title-left-${block.id}`}
                  style={{ fontWeight: 600, marginBottom: 6, ...roles.title }}
                >
                  {richText(block.column1Title)}
                </div>
              )}
              {columnBody('left', block.column1Content)}
            </div>
            <div
              data-testid={`col2-right-${block.id}`}
              style={{
                flex: '1 1 240px',
                fontSize: 15,
                ...(block.column2Align
                  ? { textAlign: block.column2Align }
                  : {}),
              }}
            >
              {hasText(block.column2Title) && (
                <div
                  data-testid={`col2-title-right-${block.id}`}
                  style={{ fontWeight: 600, marginBottom: 6, ...roles.title }}
                >
                  {richText(block.column2Title)}
                </div>
              )}
              {columnBody('right', block.column2Content)}
            </div>
          </div>
        );
      }

      case 'testimonials': {
        // WEB-1692: имя автора отдельно от текста отзыва (репорт владельца
        // 24.08). Поля живут НА БЛОКЕ — как и рамка отзыва (WEB-1825): владелец
        // просил разделение по РОЛЯМ, а не по карточкам, поэтому настройка
        // действует на каждый отзыв блока разом. Звёзды отзыва под настройку не
        // попадают (решение записано в semanticTextRoles.ts).
        const roles = blockTextRoles(block, 'testimonials');
        // Слово владельца 21.10.2026: фото автора, отметка, фон карточки,
        // кавычки и курсив по выбору — карточка вынесена в TestimonialCard
        // (у неё своё состояние: незагрузившееся фото убирается). Нет новых
        // полей — прежняя разметка карточки.
        const look = resolveTestimonialLook(block);
        return (
          <div style={{ marginBottom: 16 }}>
            {(block.testimonialItems ?? []).map((item, i) => (
              <TestimonialCard
                key={i}
                item={item}
                index={i}
                blockId={block.id}
                border={block}
                look={look}
                roles={roles}
                varCtx={varCtx}
              />
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  })();

  return withExtendedBlockShell(block, rendered, followsExplicitSpacing);
}
